import { Tooltip } from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import { useFormattedDate } from "@jugl-web/utils/hooks/useFormattedDate";
import { useIntervalRerender } from "@jugl-web/utils/hooks/useIntervalRerender";
import { HeaderIconButton } from "@web-src/modules/client-support/components/HeaderIconButton";
import { useTicketsViewContext } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/TicketsViewProvider";
import { FC } from "react";
import { Subject } from "rxjs";
import { ReactComponent as RefreshIcon } from "./assets/refresh.svg";

// oxlint-disable-next-line react/only-export-components
export const onBeforeRefresh$ = new Subject<void>();

export const TicketsViewRefreshButton: FC = () => {
  const { ticketListQuery } = useTicketsViewContext();

  const { reset, isFetching, isLoadingMore, fulfilledTimestamp } =
    ticketListQuery;

  const isRefreshing = isFetching && !isLoadingMore;

  const { formatDateTimeLabel, humanizePastDate } = useFormattedDate();
  const { t } = useTranslations();

  const humanizedLastRefreshDate = humanizePastDate(
    new Date(fulfilledTimestamp ?? 0),
    { concise: true }
  );

  // Re-render every minute to update the last refresh date label
  useIntervalRerender(60 * 1000);

  const labelContent = isRefreshing ? (
    t({
      id: "common.refreshing-ellipsis",
      defaultMessage: "Refreshing...",
    })
  ) : (
    <span className="text-dark-700">
      {t(
        {
          id: "tasks-page.refreshed-x-time-ago",
          defaultMessage: "Refreshed: {lastRefreshDate}",
        },
        {
          lastRefreshDate: (
            <span className="text-dark-800">{humanizedLastRefreshDate}</span>
          ),
        }
      )}
    </span>
  );

  const handleClick = async () => {
    onBeforeRefresh$.next();

    try {
      await reset();
    } catch {
      // Do nothing
    }
  };

  return (
    <Tooltip
      placement="bottom"
      isDisabled={isRefreshing}
      renderTrigger={({ props, ref }) => (
        <div ref={ref} {...props}>
          <HeaderIconButton
            icon={
              <RefreshIcon
                className={cx(
                  "shrink-0 transition-colors",
                  isRefreshing ? "animate-spin text-primary" : "text-dark"
                )}
              />
            }
            label={labelContent}
            disabled={isRefreshing}
            onClick={handleClick}
          />
        </div>
      )}
    >
      {formatDateTimeLabel(fulfilledTimestamp ?? 0)}
    </Tooltip>
  );
};
